// src/components/PanelSwitcher.js
// Button component for switching between Creator Panel, Customer Panel, and Student Portal

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Button,
    Box,
    Typography,
    Menu,
    MenuItem,
    ListItemIcon,
    Chip,
    Divider,
    IconButton,
    Tooltip,
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import SchoolIcon from '@mui/icons-material/School';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { usePanel, PanelType } from '../contexts/PanelContext';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const colors = {
    textPrimary: '#1e293b',
    textSecondary: '#475569',
    textTertiary: '#94a3b8',
};

const PANEL_CONFIG = {
    [PanelType.CREATOR]: {
        label: 'Creator Panel',
        shortLabel: 'Creator',
        icon: DashboardIcon,
        color: '#3b82f6',
        defaultRoute: '/chatbots',
    },
    [PanelType.CUSTOMER]: {
        label: 'Customer Panel',
        shortLabel: 'Customer',
        icon: PeopleIcon,
        color: '#10b981',
        defaultRoute: '/customer-panel/dashboard',
    },
    [PanelType.STUDENT]: {
        label: 'Student Portal',
        shortLabel: 'Student',
        icon: SchoolIcon,
        color: '#8b5cf6',
        defaultRoute: '/student-portal/dashboard',
    },
    [PanelType.TRADING]: {
        label: 'Trading Portal',
        shortLabel: 'Stock',
        icon: TrendingUpIcon,
        color: '#f59e0b',
        defaultRoute: '/trading-portal/dashboard',
    },
};

export default function PanelSwitcher() {
    const navigate = useNavigate();
    const {
        activePanel,
        switchPanel,
        canSwitchPanels,
        panelAccess,
    } = usePanel();

    const muiTheme = useTheme();
    const isMobileScreen = useMediaQuery(muiTheme.breakpoints.down('md'));
    const [anchorEl, setAnchorEl] = useState(null);
    const menuOpen = Boolean(anchorEl);

    if (!canSwitchPanels()) {
        return null;
    }

    // All accessible panels
    const accessiblePanels = Object.values(PanelType).filter(
        panel => panelAccess[panel]
    );

    const activeConfig = PANEL_CONFIG[activePanel] || PANEL_CONFIG[PanelType.CREATOR];
    const ActiveIcon = activeConfig.icon;
    const activeColor = activeConfig.color;

    const handleOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleSwitch = (targetPanel) => {
        handleClose();
        if (targetPanel === activePanel) return;
        const success = switchPanel(targetPanel);
        if (success) {
            const config = PANEL_CONFIG[targetPanel];
            navigate(config.defaultRoute);
        }
    };

    return (
        <Box sx={{ mr: { xs: 0, md: 1 } }}>
            {/* Mobile: icon-only button */}
            <Tooltip title={activeConfig.label}>
                <IconButton
                    onClick={handleOpen}
                    sx={{
                        display: { xs: 'flex', md: 'none' },
                        width: 36,
                        height: 36,
                        borderRadius: '10px',
                        bgcolor: `${activeColor}14`,
                        border: `1px solid ${activeColor}26`,
                        '&:hover': { bgcolor: `${activeColor}1f` },
                    }}
                >
                    <ActiveIcon sx={{ fontSize: '1.1rem', color: activeColor }} />
                </IconButton>
            </Tooltip>
            {/* Desktop: full button */}
            <Button
                onClick={handleOpen}
                startIcon={
                    <Box sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '8px',
                        background: `linear-gradient(135deg, ${activeColor}, ${activeColor}dd)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: `0 2px 6px ${activeColor}4d`,
                    }}>
                        <ActiveIcon sx={{ fontSize: '1rem', color: '#fff' }} />
                    </Box>
                }
                endIcon={
                    <ExpandMoreIcon
                        sx={{
                            fontSize: '1rem',
                            transition: 'transform 0.2s ease',
                            transform: menuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        }}
                    />
                }
                sx={{
                    display: { xs: 'none', md: 'inline-flex' },
                    fontFamily,
                    fontSize: '0.8rem',
                    textTransform: 'none',
                    color: colors.textPrimary,
                    bgcolor: `${activeColor}14`,
                    borderRadius: '12px',
                    px: 1.5,
                    py: 0.75,
                    minWidth: 160,
                    justifyContent: 'space-between',
                    border: `1px solid ${activeColor}26`,
                    '&:hover': {
                        bgcolor: `${activeColor}1f`,
                        borderColor: `${activeColor}40`,
                    },
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flex: 1, ml: 0.5 }}>
                    <Typography sx={{ fontFamily, fontSize: '0.65rem', fontWeight: 600, color: activeColor, textTransform: 'uppercase', letterSpacing: '0.05em', lineHeight: 1 }}>
                        Panel
                    </Typography>
                    <Typography sx={{ fontFamily, fontSize: '0.75rem', fontWeight: 700, color: colors.textPrimary, lineHeight: 1.2 }}>
                        {activeConfig.shortLabel}
                    </Typography>
                </Box>
            </Button>

            <Menu
                anchorEl={anchorEl}
                open={menuOpen}
                onClose={handleClose}
                PaperProps={{
                    sx: {
                        minWidth: { xs: 200, sm: 240 },
                        maxWidth: { xs: 260, sm: 'none' },
                        maxHeight: { xs: '60vh', sm: 'none' },
                        mt: 1,
                        borderRadius: { xs: '10px', sm: '12px' },
                        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                        border: '1px solid rgba(0,0,0,0.06)',
                        overflowY: 'auto',
                        '&::-webkit-scrollbar': { width: '3px' },
                        '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.15)', borderRadius: '4px' },
                    }
                }}
            >
                <Box sx={{
                    px: { xs: 1.5, sm: 2 },
                    py: { xs: 1, sm: 1.5 },
                    borderBottom: '1px solid rgba(0,0,0,0.06)',
                }}>
                    <Typography sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 600, color: colors.textTertiary, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Switch Panel ({accessiblePanels.length})
                    </Typography>
                </Box>

                <Box sx={{ py: { xs: 0.5, sm: 1 } }}>
                    {accessiblePanels.map(panel => {
                        const config = PANEL_CONFIG[panel];
                        const Icon = config.icon;
                        const isActive = panel === activePanel;

                        return (
                            <MenuItem
                                key={panel}
                                onClick={() => handleSwitch(panel)}
                                sx={{
                                    fontFamily,
                                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                    py: { xs: 1, sm: 1.5 },
                                    px: { xs: 1.5, sm: 2 },
                                    mx: { xs: 0.5, sm: 1 },
                                    borderRadius: { xs: '8px', sm: '10px' },
                                    minHeight: 'auto',
                                    bgcolor: isActive ? `${config.color}1a` : 'transparent',
                                    '&:hover': { bgcolor: isActive ? `${config.color}26` : 'rgba(0,0,0,0.04)' },
                                }}
                            >
                                <Box sx={{
                                    width: { xs: 26, sm: 32 },
                                    height: { xs: 26, sm: 32 },
                                    borderRadius: { xs: '6px', sm: '8px' },
                                    bgcolor: isActive ? config.color : colors.textTertiary,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    mr: { xs: 1, sm: 1.5 },
                                    flexShrink: 0,
                                }}>
                                    <Icon sx={{ fontSize: { xs: '0.85rem', sm: '1rem' }, color: '#fff' }} />
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography sx={{
                                        fontFamily,
                                        fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                        fontWeight: isActive ? 600 : 500,
                                        color: isActive ? config.color : colors.textPrimary,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}>
                                        {config.label}
                                    </Typography>
                                    {isActive && (
                                        <Chip
                                            label="Active"
                                            size="small"
                                            sx={{
                                                height: { xs: 16, sm: 18 },
                                                mt: 0.5,
                                                fontSize: '0.6rem',
                                                bgcolor: `${config.color}26`,
                                                color: config.color,
                                            }}
                                        />
                                    )}
                                </Box>
                                {isActive && (
                                    <CheckCircleIcon sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem' }, color: config.color, ml: 0.5 }} />
                                )}
                            </MenuItem>
                        );
                    })}
                </Box>
            </Menu>
        </Box>
    );
}
